import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/PageHero";
import pageCover from "@/assets/kupish/p-pogosta-vprasanja.webp";

const faqs = [
  {
    q: "Kaj je kupish.si?",
    a: "kupish.si je slovensko spletno nakupovalno središče novih izdelkov. Na enem mestu združuje ponudbo različnih spletnih trgovin in ponudnikov — od elektronike, avdia in avtoopreme do drogerije, doma in vrta, mode ter športa.",
  },
  {
    q: "Kako lahko kupim izdelek?",
    a: "Izdelek dodajte v košarico, izpolnite podatke za dostavo in oddajte naročilo. Naročilo prejme ponudnik izdelka, ki vam izda predračun oziroma račun in poskrbi za odpremo.",
  },
  {
    q: "Ali lahko v enem naročilu kupim izdelke več trgovin?",
    a: "Da. Košarica lahko vsebuje izdelke različnih ponudnikov. Naročilo se razdeli po prodajalcih, zato lahko pošiljke prispejo ločeno.",
  },
  {
    q: "Koliko stane dostava?",
    a: "Pogoje dostave določi vsak ponudnik sam in so navedeni pri izdelku oziroma v košarici. Nekateri ponudniki imajo strošek dostave že vključen v ceno izdelka.",
  },
  {
    q: "Kakšen je dobavni rok?",
    a: "Izdelki so novi in običajno na zalogi, zato je dobavni rok najpogosteje 1–5 delovnih dni. Pri izdelkih po naročilu ali z daljšo dobavo je rok naveden pri izdelku.",
  },
  {
    q: "Ali lahko izdelek vrnem?",
    a: "Kot potrošnik imate v skladu z Zakonom o varstvu potrošnikov 14 dni za odstop od pogodbe brez navedbe razloga. Izjeme so izdelki, izdelani po naročilu ali prilagojeni vašim željam, in odpečateni izdelki, ki jih zaradi higiene ni mogoče vrniti.",
  },
  {
    q: "Kdo je prodajalec in kdo izda račun?",
    a: "Prodajalec je ponudnik, ki je izdelek objavil — njegovo ime je navedeno pri vsakem izdelku. Ponudnik izda račun, odpošlje izdelek in odgovarja za garancijo ter reklamacije. kupish.si upravlja nakupovalno središče in tehnično platformo.",
  },
  {
    q: "Kako postanem prodajalec na kupish.si?",
    a: "Registrirajte se, izpolnite profil trgovine in dodajte izdelke. Trgovina postane javno vidna po plačilu letne najemnine. Podrobnosti so na strani Najem spletne trgovine.",
  },
  {
    q: "Koliko stane najem spletne trgovine?",
    a: "Najem stane 240 € letno in vključuje objavo do 100 izdelkov, profil trgovine, kontaktne podatke, optimizacijo vsebin in vzdrževanje. Provizije od prodaje ne zaračunavamo.",
  },
  {
    q: "Ali so izdelki novi?",
    a: "Da. Na kupish.si se prodajajo novi izdelki. Rabljenih in nadomestnih izdelkov ne objavljamo.",
  },
  {
    q: "Kako se obrnem na ponudnika?",
    a: "Na strani vsakega izdelka in na profilu trgovine je gumb za povpraševanje. Ponudniki običajno odgovorijo v enem delovnem dnevu.",
  },
  {
    q: "Kako uveljavim garancijo ali reklamacijo?",
    a: "Reklamacijo naslovite na ponudnika, pri katerem ste izdelek kupili. Postopek in roki so opisani na strani Reševanje reklamacij.",
  },
];


const url = "https://kupish.si/pogosta-vprasanja";

export const Route = createFileRoute("/pogosta-vprasanja")({
  head: () => ({
    meta: [
      { title: "Pogosta vprašanja (FAQ) | kupish.si" },
      { name: "description", content: "Odgovori na pogosta vprašanja o nakupu na kupish.si: dostava, dobavni roki, vračila, garancija, kdo je prodajalec in kako poteka najem spletne trgovine." },
      { property: "og:title", content: "Pogosta vprašanja (FAQ) | kupish.si" },
      { property: "og:description", content: "Odgovori na pogosta vprašanja o nakupu, dostavi, vračilih, garanciji in najemu spletne trgovine." },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "sl_SI" },
      { property: "og:site_name", content: "kupish.si" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Pogosta vprašanja (FAQ) | kupish.si" },
      { name: "twitter:description", content: "Odgovori na pogosta vprašanja o nakupu, dostavi, vračilih, garanciji in najemu spletne trgovine." },

    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "sl-SI",
          url,
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "[data-answer]"] },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <section className="pb-16 md:pb-24">
      <PageHero src={pageCover} alt="Pogosta vprašanja kupish.si — minimalističen 3D prikaz vprašanja in odgovora">
        <div className="eyebrow">Pomoč</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-3">Pogosta vprašanja</h1>
        <p data-answer className="mt-4 leading-relaxed max-w-3xl">
          kupish.si je slovensko spletno nakupovalno središče novih izdelkov različnih ponudnikov in
          spletnih trgovin. Nakup poteka prek košarice, dobavni rok je običajno 1–5 delovnih
          dni, vračilo pa je mogoče v 14 dneh (razen pri izdelkih po naročilu). Spodaj so
          odgovori na najpogostejša vprašanja kupcev in ponudnikov.
        </p>
      </PageHero>
      <div className="mx-auto max-w-3xl px-6">

      <div className="mt-12 divide-y divide-border border-y border-border">
        {faqs.map((f) => (
          <details key={f.q} className="group py-5">
            <summary className="cursor-pointer list-none font-serif text-lg md:text-xl flex justify-between items-start gap-4">
              <span>{f.q}</span>
              <span className="text-accent transition-transform group-open:rotate-45 shrink-0">+</span>
            </summary>
            <div className="mt-3 text-muted-foreground leading-relaxed">{f.a}</div>
          </details>
        ))}
      </div>

      <div className="mt-12 text-sm text-muted-foreground">
        Niste našli odgovora? Pišite nam preko{" "}
        <Link to="/o-nas" className="underline underline-offset-4 hover:text-accent">kontaktne strani</Link>.
      </div>
      </div>
    </section>
  );
}
